import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { useT } from '../lib/i18n';
import { publicAPI, checkoutAPI } from '../lib/api';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernServiceDetail — the «Moderno» theme service page (/service/:id).
// Approved by Anthony (s140): the "Editorial Banner" header + article with a
// sticky graphite card (SD1) and the "Other services" cards (from SD3). Data = the service (title, short_description,
// full_content, image, banner_image, price/stripe, external_url) + the other
// visible services. Legacy rich text is normalised for display only: inline
// colours follow the brand and the old 14pt "headings" read as sub-headings.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const GRAPHITE = '#16171a';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const plain = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

const CSS = `
.msd-prose{font-size:1.0625rem;line-height:1.78;color:var(--color-body-text,#475569);overflow-wrap:break-word}
.msd-prose p{margin:0 0 1.1em}
.msd-prose [style*="color"]{color:inherit!important}
.msd-prose strong,.msd-prose b{color:var(--color-heading,#1a2332)}
.msd-prose a{color:var(--accent-strong,#b86500);text-decoration:underline;text-underline-offset:.3em}
.msd-prose h2,.msd-prose h3,.msd-prose .msd-h{display:block;font-family:${SERIF};font-weight:600;color:var(--color-heading,#1a2332)!important;font-size:1.4rem!important;line-height:1.3;margin:1.8em 0 .45em;scroll-margin-top:110px}
.msd-prose .msd-h+br{display:none}
.msd-prose ul,.msd-prose ol{padding-left:1.2em;margin:0 0 1.1em}
.msd-prose ul{list-style:disc}
.msd-prose ol{list-style:decimal}
.msd-prose ul ul{list-style:circle}
`;

// Marks legacy pseudo-headings (a font-size span of 13pt+) and real h2/h3 as
// sub-headings with ids, on the HTML string before it is rendered (React would
// discard classes added to the DOM afterwards). Returns { html, sections }.
function useSections(raw) {
  return React.useMemo(() => {
    if (!raw || typeof DOMParser === 'undefined') return { html: raw || '', sections: [] };
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
    const sections = [];
    doc.body.querySelectorAll('h2, h3, span[style*="font-size"]').forEach((n, i) => {
      if (n.tagName === 'SPAN') {
        const size = parseFloat((n.getAttribute('style') || '').match(/font-size:\s*([\d.]+)/)?.[1] || 0);
        if (size < 13) return;
      }
      const text = n.textContent.trim(); if (!text) return;
      n.classList.add('msd-h');
      n.id = n.id || `section-${i}`;
      sections.push({ id: n.id, text });
    });
    return { html: doc.body.firstChild.innerHTML, sections };
  }, [raw]);
}

function Cta({ s, dark = false, full = false }) {
  const tt = useT();
  const cls = `mx-btn mx-btn--primary ${dark ? 'mx-dark ' : ''}inline-flex items-center justify-center gap-2 font-semibold text-sm px-6 py-3.5 rounded-[2px] ${full ? 'w-full' : ''}`;
  const style = { background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' };
  if (s.external_url) return <a href={s.external_url} target={s.open_in_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className={cls} style={style} data-testid="service-cta">Learn more <ArrowRight className="w-4 h-4" /></a>;
  if (s.price > 0 && s.stripe_price_id) {
    const buy = async () => {
      try { const r = await checkoutAPI.create({ price_id: s.stripe_price_id, service_name: tt(s.title), amount: s.price }); if (r.data.url) window.location.href = r.data.url; }
      catch { toast.error('Checkout error'); }
    };
    return <button type="button" onClick={buy} className={cls} style={style} data-testid="service-cta">Get started · ${Number(s.price).toFixed(0)} <ArrowRight className="w-4 h-4" /></button>;
  }
  return <Link to="/#contact" className={cls} style={style} data-testid="service-cta">Talk to our team <ArrowRight className="w-4 h-4" /></Link>;
}

function Others({ list }) {
  const tt = useT();
  if (!list.length) return null;
  return (
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {list.slice(0, 3).map(o => (
        <Link key={o.id} to={`/service/${o.id}`} className="group flex flex-col gap-3" data-testid={`other-service-${o.id}`}>
          <span className="block aspect-[4/3] overflow-hidden rounded-[3px]" style={{ background: GRAPHITE }}>
            {o.image && <img src={img(o.image)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />}
          </span>
          <span className="font-semibold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: '1.25rem', color: 'var(--color-heading, #1a2332)' }}>{tt(o.title)}</span>
          <span className="text-[15px] leading-relaxed line-clamp-2" style={{ color: 'var(--color-body-text, #475569)' }}>{plain(tt(o.short_description) || tt(o.description))}</span>
        </Link>
      ))}
    </div>
  );
}

const Eyebrow = ({ children, light }) => (
  <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: light ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)' }}>
    <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{children}
  </div>
);

const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';

/* ── Editorial Banner ────────────────────────────────────────────────────
   Full-width banner with the title over it; the article beside a sticky
   graphite card (photo + call to action); the other services as cards. */
function BannerVariant({ s, html, others, lead }) {
  const tt = useT();
  const { html: body } = useSections(html);
  const banner = img(s.banner_image || s.image);
  return (
    <>
      <header className="relative isolate overflow-hidden min-h-[360px] lg:min-h-[440px] flex items-end" style={{ background: GRAPHITE }}>
        {banner && <img src={banner} alt="" className="absolute inset-0 -z-10 w-full h-full object-cover" />}
        <span className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(90deg, rgba(10,11,14,.9) 0%, rgba(10,11,14,.62) 55%, rgba(10,11,14,.25) 100%)' }} />
        <div className={`${shell} w-full pt-32 pb-12 lg:pb-16 flex flex-col gap-4`}>
          <Link to="/#services" className="mx-link mx-dark inline-flex items-center gap-2 text-sm font-semibold self-start text-white/80"><ArrowLeft className="w-4 h-4" /> All services</Link>
          <Eyebrow light>Service</Eyebrow>
          <h1 className="font-bold text-white max-w-3xl" style={{ fontFamily: SERIF, fontSize: 'clamp(2.2rem, 4.4vw, 3.6rem)', lineHeight: 1.06, textWrap: 'balance' }}>{tt(s.title)}</h1>
          {lead && <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl line-clamp-3">{lead}</p>}
        </div>
      </header>
      <div className={`${shell} py-16 lg:py-24 grid gap-12 lg:gap-16 lg:grid-cols-[minmax(0,1fr)_360px] items-start`}>
        <article className="msd-prose max-w-[68ch]" dangerouslySetInnerHTML={{ __html: body }} />
        <aside className="lg:sticky lg:top-28 flex flex-col gap-6 p-6 lg:p-7 rounded-[3px]" style={{ background: GRAPHITE }}>
          {s.image && <img src={img(s.image)} alt="" className="w-full aspect-[4/3] object-cover rounded-[2px]" />}
          <div className="flex flex-col gap-2">
            <span className="text-white font-semibold" style={{ fontFamily: SERIF, fontSize: '1.3rem' }}>Work with us</span>
            <span className="text-white/65 text-sm leading-relaxed">Tell us about your project and a member of the team will get back to you.</span>
          </div>
          <Cta s={s} dark full />
        </aside>
      </div>
      {others.length > 0 && (
        <section className="border-t" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
          <div className={`${shell} py-16 lg:py-24 flex flex-col gap-10`}>
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', color: 'var(--color-heading, #1a2332)' }}>Other <em style={{ fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>services</em></h2>
            <Others list={others} />
          </div>
        </section>
      )}
    </>
  );
}

export default function ModernServiceDetail({ service: s }) {
  const tt = useT();
  const [all, setAll] = React.useState([]);
  React.useEffect(() => { publicAPI.getServices().then(r => setAll((r.data || []).filter(o => o.visible !== false))).catch(() => {}); }, []);
  const html = tt(s.full_content) || tt(s.description) || '';
  const lead = plain(tt(s.short_description) || (tt(s.full_content) ? tt(s.description) : ''));
  const others = all.filter(o => o.id !== s.id);
  return (
    <div data-testid="service-detail-page" className="bg-white">
      <style>{CSS}</style>
      <BannerVariant s={s} html={html} others={others} lead={lead} />
    </div>
  );
}
